import { useMemo, useState } from "react";
import Decimal from "decimal.js";
import moment from "moment-timezone";
import { useToast } from "@/components/ui/use-toast";
import {
  TtOrderMetadata,
  TtOrderMetadataCustomerSplit,
  TtOrderSnapshot,
  useUpsertOrderMetadataV2,
} from "@/lib/api/risk/brokerage/tt";
import { Commodities } from "@/lib/api/commodity";
import { CommonDestinationTimezone } from "@/lib/utils";
import { getSuggestedCustomerId } from "@/lib/tt/suggestCustomer";
import { getSuggestedOrderType } from "@/lib/tt/suggestOrderType";
import { metadataToOrderType, orderTypeToExpectedFlags } from "@/lib/tt/orderType";
import {
  AttachFormValues,
  CustomerSplitEntry,
  OrderType,
  OtcMode,
  REOWN_CONTRACT_TYPES,
} from "./types";

/**
 * Commodities for which the v2 backend route allows over-allocation by up to
 * 2500 UOM (absolute), but disallows under-allocation. Mirrors
 * OVER_ALLOCATION_COMMODITIES on the backend.
 */
const OVER_ALLOCATION_COMMODITIES = new Set<string>([
  Commodities.Corn,
  Commodities.Soybeans,
  Commodities.MinneapolisWheat,
  Commodities.WinterWheat,
]);

const MAX_OVER_ALLOCATION = new Decimal(2500);

export interface AttachOrderFormSuggestions {
  customerId?: string;
  orderType?: OrderType;
}

export interface UseAttachOrderFormOptions {
  guidOrderId: string;
  snapshot: TtOrderSnapshot;
  existingMetadata: TtOrderMetadata | null;
  /**
   * Customer options provided by the parent component. Required for the
   * customer-suggestion engine. Pass [] when the customer list is still
   * loading; suggestions will simply be undefined until it arrives.
   */
  customers: Array<{ id: string; longName: string; hasRiskProfile?: boolean }>;
  onSaved?: () => void;
}

export interface UseAttachOrderFormResult {
  values: AttachFormValues;
  /** Snapshot of the values computed from existingMetadata at mount, never mutated. */
  originalValues: AttachFormValues;
  dirtyFields: Set<keyof AttachFormValues>;
  suggestions: AttachOrderFormSuggestions;
  /** Pre-computed totals for UI. */
  totals: {
    totalQuantity: Decimal | null;
    assigned: Decimal;
    lhcRemainder: Decimal;
  };
  errors: Partial<Record<keyof AttachFormValues, string>>;
  isValid: boolean;
  isSubmitting: boolean;
  setField: <K extends keyof AttachFormValues>(key: K, value: AttachFormValues[K]) => void;
  changeCustomerSplitQuantity: (index: number, value: string) => void;
  changeCustomerSplitCustomer: (index: number, customerId: string) => void;
  addCustomerSplit: () => void;
  removeCustomerSplit: (index: number) => void;
  applyCustomerSuggestion: () => void;
  applyOrderTypeSuggestion: () => void;
  submit: () => Promise<void>;
}

let __keySeq = 0;
function makeKey(): string {
  __keySeq += 1;
  return `s${__keySeq}_${Date.now()}`;
}

function getOtcModeFromMetadata(meta: TtOrderMetadata | null): OtcMode | null {
  const t = meta?.otc_contract_type;
  if (!t) return null;
  return REOWN_CONTRACT_TYPES.has(t) ? "reown" : "hedgeable";
}

/** Convert a split_percentage (0–100) to a quantity given a totalQuantity. */
function percentageToQuantity(pct: number | string, total: Decimal): Decimal {
  return new Decimal(pct).div(100).mul(total).toDecimalPlaces(0);
}

function buildOriginalValues({
  snapshot,
  metadata,
}: {
  snapshot: TtOrderSnapshot;
  metadata: TtOrderMetadata | null;
}): AttachFormValues {
  const total = snapshot.order_uom_quantity != null ? new Decimal(snapshot.order_uom_quantity) : null;
  const currentYear = moment.tz(CommonDestinationTimezone).format("YYYY");

  const splits: CustomerSplitEntry[] = (() => {
    if (metadata?.SpecifiedCustomers && metadata.SpecifiedCustomers.length > 0) {
      return metadata.SpecifiedCustomers.map((c: TtOrderMetadataCustomerSplit) => ({
        key: makeKey(),
        customer_id: c.customer_id,
        quantity: total != null
          ? percentageToQuantity(c.split_percentage, total).toFixed(0)
          : String(c.split_percentage),
      }));
    }
    return [{
      key: makeKey(),
      customer_id: "",
      quantity: total != null ? total.toFixed(0) : "",
    }];
  })();

  return {
    customerSplits: splits,
    cropYear: metadata?.crop_year && metadata.crop_year !== 0
      ? String(metadata.crop_year)
      : currentYear,
    orderType: metadataToOrderType(metadata),
    otcMode: getOtcModeFromMetadata(metadata),
    otcContractType: metadata?.otc_contract_type ?? "",
    shouldIgnore: metadata?.should_ignore_order ?? false,
    isNonClient: false,
  };
}

function sumQuantities(splits: CustomerSplitEntry[]): Decimal {
  return splits.reduce((sum, s) => {
    try {
      return s.quantity ? sum.plus(new Decimal(s.quantity)) : sum;
    } catch {
      return sum;
    }
  }, new Decimal(0));
}

function recalculateFirstRowQuantity({
  splits,
  totalQuantity,
}: {
  splits: CustomerSplitEntry[];
  totalQuantity: Decimal | null;
}): CustomerSplitEntry[] {
  if (totalQuantity == null || splits.length <= 1) {
    return splits;
  }

  const otherSum = splits.slice(1).reduce((sum, entry) => {
    try {
      return entry.quantity ? sum.plus(new Decimal(entry.quantity)) : sum;
    } catch {
      return sum;
    }
  }, new Decimal(0));

  const remainder = Decimal.max(0, totalQuantity.minus(otherSum));
  return [{ ...splits[0], quantity: remainder.toFixed(0) }, ...splits.slice(1)];
}

export function useAttachOrderForm({
  guidOrderId,
  snapshot,
  existingMetadata,
  customers,
  onSaved,
}: UseAttachOrderFormOptions): UseAttachOrderFormResult {
  const { toast } = useToast();
  const upsertMetadata = useUpsertOrderMetadataV2();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [firstRowUserEdited, setFirstRowUserEdited] = useState(false);

  // Recompute originalValues only when the underlying order changes (by guid).
  const originalValues = useMemo(
    () => buildOriginalValues({ snapshot, metadata: existingMetadata }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [guidOrderId]
  );

  const [values, setValues] = useState<AttachFormValues>(originalValues);
  const [dirtyFields, setDirtyFields] = useState<Set<keyof AttachFormValues>>(new Set());

  // Reset state when the underlying order changes.
  // (useState above only initializes on mount; this hook needs to reset when
  //  the consumer remounts the form by changing key. We require parents to
  //  remount the form via key={guidOrderId} so this stays simple.)

  const suggestionCustomers = useMemo(
    () => customers.filter((customer) => {
      if (values.isNonClient) return !customer.hasRiskProfile;
      return !!customer.hasRiskProfile;
    }),
    [customers, values.isNonClient]
  );

  const suggestions = useMemo<AttachOrderFormSuggestions>(() => {
    if (existingMetadata) {
      // Don't suggest over existing data; show only fresh suggestions for unset fields.
      // (Behavior preserved from v1.)
    }
    const customerId = getSuggestedCustomerId({
      orderTextFields: {
        textA: snapshot.text_a,
        textB: snapshot.text_b,
        textC: snapshot.text_c,
        textTT: snapshot.text_tt,
      },
      customers: suggestionCustomers,
    });
    const orderType = getSuggestedOrderType({
      textA: snapshot.text_a,
      textB: snapshot.text_b,
      textC: snapshot.text_c,
      textTT: snapshot.text_tt,
    });
    return {
      customerId,
      orderType: orderType ?? undefined,
    };
  }, [snapshot, suggestionCustomers, existingMetadata]);

  const totalQuantity = useMemo<Decimal | null>(
    () => snapshot.order_uom_quantity != null ? new Decimal(snapshot.order_uom_quantity) : null,
    [snapshot.order_uom_quantity]
  );

  const assigned = useMemo(() => sumQuantities(values.customerSplits), [values.customerSplits]);
  const lhcRemainder = useMemo(
    () => totalQuantity != null ? Decimal.max(0, totalQuantity.minus(assigned)) : new Decimal(0),
    [totalQuantity, assigned]
  );

  const errors = useMemo<Partial<Record<keyof AttachFormValues, string>>>(() => {
    const out: Partial<Record<keyof AttachFormValues, string>> = {};

    const firstCustomerId = values.customerSplits[0]?.customer_id ?? "";
    const hasNamedCustomer = values.customerSplits.some((s: CustomerSplitEntry) => s.customer_id && s.quantity);
    const hasLhc = lhcRemainder.gt(0);

    const commodityId = snapshot.commodity_id;
    const isCanola = commodityId === Commodities.Canola;
    const isOverAllocation = commodityId != null && OVER_ALLOCATION_COMMODITIES.has(commodityId);

    if (!firstCustomerId) {
      out.customerSplits = "Please select at least one customer.";
    } else if (!hasNamedCustomer && !hasLhc) {
      out.customerSplits = "Select at least one customer or leave a remainder for LHC.";
    } else if (isCanola) {
      // No quantity bounds for canola.
    } else if (isOverAllocation && totalQuantity != null) {
      const maxAllowed = totalQuantity.plus(MAX_OVER_ALLOCATION);
      if (assigned.gt(maxAllowed)) {
        out.customerSplits = `Assigned quantity (${assigned.toFixed(0)}) must not exceed order quantity (${totalQuantity.toFixed(0)}) by more than 2500.`;
      }
      // Note: assigned >= total is naturally satisfied because
      // lhcRemainder = max(0, total - assigned) is always added back via
      // the LHC row at submit time. The "customers > total => lhc must be 0"
      // backend rule is also auto-satisfied: when customers_sum > total,
      // lhcRemainder is 0 by definition (max with 0).
    } else if (totalQuantity != null && assigned.gt(totalQuantity)) {
      out.customerSplits = `Customer quantities (${assigned.toFixed(0)}) exceed total (${totalQuantity.toFixed(0)}).`;
    }

    if (!values.cropYear) {
      out.cropYear = "Crop year is required.";
    }

    if (values.orderType === "none") {
      out.orderType = "Order type is required.";
    }

    if (values.orderType === "otc" && !values.otcContractType) {
      out.otcContractType = "OTC contract type is required.";
    }

    return out;
  }, [values, totalQuantity, assigned, lhcRemainder, snapshot.commodity_id]);

  const isValid = Object.keys(errors).length === 0;

  function markDirty<K extends keyof AttachFormValues>(key: K) {
    setDirtyFields((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }

  function setField<K extends keyof AttachFormValues>(key: K, value: AttachFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
    markDirty(key);
  }

  function changeCustomerSplitQuantity(index: number, value: string) {
    if (index === 0) {
      setFirstRowUserEdited(true);
    }

    setValues((prev) => {
      let nextSplits = prev.customerSplits.map((entry, i) => (
        i === index ? { ...entry, quantity: value } : entry
      ));

      if (index !== 0 && !firstRowUserEdited && totalQuantity != null) {
        nextSplits = recalculateFirstRowQuantity({
          splits: nextSplits,
          totalQuantity,
        });
      }

      return { ...prev, customerSplits: nextSplits };
    });
    markDirty("customerSplits");
  }

  function changeCustomerSplitCustomer(index: number, customerId: string) {
    setValues((prev) => {
      const nextSplits = prev.customerSplits.map((entry, i) => (
        i === index ? { ...entry, customer_id: customerId } : entry
      ));

      if (
        nextSplits.length === 1
        && !nextSplits[0]?.quantity
        && totalQuantity != null
      ) {
        return {
          ...prev,
          customerSplits: [{ ...nextSplits[0], quantity: totalQuantity.toFixed(0) }],
        };
      }

      return { ...prev, customerSplits: nextSplits };
    });
    markDirty("customerSplits");
  }

  function addCustomerSplit() {
    setValues((prev) => ({
      ...prev,
      customerSplits: [...prev.customerSplits, { key: makeKey(), customer_id: "", quantity: "" }],
    }));
    markDirty("customerSplits");
  }

  function removeCustomerSplit(index: number) {
    setValues((prev) => {
      let nextSplits = prev.customerSplits.filter((_, i) => i !== index);

      if (!firstRowUserEdited && totalQuantity != null && nextSplits.length > 1) {
        nextSplits = recalculateFirstRowQuantity({
          splits: nextSplits,
          totalQuantity,
        });
      }

      return { ...prev, customerSplits: nextSplits };
    });
    markDirty("customerSplits");
  }

  function applyCustomerSuggestion() {
    if (!suggestions.customerId) return;

    if (values.customerSplits.length === 0) {
      setValues((prev) => ({
        ...prev,
        customerSplits: [{
          key: makeKey(),
          customer_id: suggestions.customerId!,
          quantity: totalQuantity != null ? totalQuantity.toFixed(0) : "",
        }],
      }));
      markDirty("customerSplits");
      return;
    }

    changeCustomerSplitCustomer(0, suggestions.customerId);
  }

  function applyOrderTypeSuggestion() {
    if (!suggestions.orderType) return;
    setField("orderType", suggestions.orderType);
  }

  async function submit() {
    if (isSubmitting) return;

    const firstCustomerId = values.customerSplits[0]?.customer_id ?? "";
    if (!firstCustomerId) {
      toast({
        title: "Validation Error",
        description: "Please select at least one customer",
        variant: "destructive",
      });
      return;
    }

    if (!values.cropYear) {
      toast({
        title: "Validation Error",
        description: "Please select a crop year",
        variant: "destructive",
      });
      return;
    }

    if (values.orderType === "none") {
      toast({
        title: "Validation Error",
        description: "Please select a contract type",
        variant: "destructive",
      });
      return;
    }

    if (values.orderType === "otc" && !values.otcContractType) {
      toast({
        title: "Validation Error",
        description: "Please select an OTC contract type",
        variant: "destructive",
      });
      return;
    }

    const validSplits = values.customerSplits.filter((s) => s.customer_id);
    if (validSplits.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one customer",
        variant: "destructive",
      });
      return;
    }

    // Bounds check (per commodity) — server enforces too. Mirrors `errors` above.
    const commodityId = snapshot.commodity_id;
    const isCanola = commodityId === Commodities.Canola;
    const isOverAllocation = commodityId != null && OVER_ALLOCATION_COMMODITIES.has(commodityId);

    if (!isCanola && isOverAllocation && totalQuantity != null) {
      const maxAllowed = totalQuantity.plus(MAX_OVER_ALLOCATION);
      if (assigned.gt(maxAllowed)) {
        toast({
          title: "Validation Error",
          description: `Assigned quantity (${assigned.toFixed(0)}) must not exceed order quantity (${totalQuantity.toFixed(0)}) by more than 2500`,
          variant: "destructive",
        });
        return;
      }
    } else if (!isCanola && totalQuantity != null && assigned.gt(totalQuantity)) {
      toast({
        title: "Customer quantities",
        description: `(${assigned.toFixed(0)}) exceeds total order quantity (${totalQuantity.toFixed(0)})`,
        variant: "destructive",
      });
      return;
    }

    const customersUom = validSplits.map((s: CustomerSplitEntry) => ({
      customer_id: s.customer_id,
      uom_quantity: s.quantity || "0",
    }));

    const lhcUomQuantity: string | null = lhcRemainder.gt(0) ? lhcRemainder.toString() : null;

    setIsSubmitting(true);
    try {
      const result = await upsertMetadata.mutateAsync({
        guid_order_id: guidOrderId,
        ...orderTypeToExpectedFlags(values.orderType),
        otc_contract_type: values.orderType === "otc" ? values.otcContractType : undefined,
        should_ignore_order: values.shouldIgnore,
        crop_year: parseInt(values.cropYear),
        lhc_uom_quantity: lhcUomQuantity,
        customers: customersUom,
      });

      if (result.success) {
        toast({ title: "Saved", description: "Order metadata updated successfully." });
        onSaved?.();
      } else {
        throw new Error(result.message || "Failed to update metadata");
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to update metadata";
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  }

  return {
    values,
    originalValues,
    dirtyFields,
    suggestions,
    totals: { totalQuantity, assigned, lhcRemainder },
    errors,
    isValid,
    isSubmitting,
    setField,
    changeCustomerSplitQuantity,
    changeCustomerSplitCustomer,
    addCustomerSplit,
    removeCustomerSplit,
    applyCustomerSuggestion,
    applyOrderTypeSuggestion,
    submit,
  };
}
